<hlm-dialog-surface size="sm">
  <hlm-dialog-header>
    <div hlmDialogHeading>
      <!-- Title names the section (Usage Credit = KV "Nutzungsguthaben"); the entry may add
           (General) or write off (Expired), so neither title nor submit says "Add" (EB-4128) -->
      <h2 hlmDialogTitle class="font-display text-xl">New Usage Credit Entry</h2>
    </div>
    <span class="ml-auto"></span>
    <button hlmDialogClose tabindex="-1" (click)="cancel()">
      <hlm-icon name="lucideX" size="1rem" />
    </button>
  </hlm-dialog-header>

  <section hlmDialogBody>
    <p class="mb-4 text-sm text-muted-foreground">
      Customer {{ data.customerId }} — the credit is customer-wide.
    </p>

    <form [formGroup]="form" (ngSubmit)="submit()" class="flex flex-col gap-5">
      <!-- TYPE — segmented toggle (EB-4128). Native radios sharing name="type" keep the
           arrow-key group + "1 of 2" semantics (Angular's radio accessor does NOT set the
           name attribute); the tile carries sign, colour and the German term. -->
      <fieldset data-testid="credit-type" class="flex flex-col gap-2">
        <legend class="cmt-eyebrow mb-2">Type</legend>
        <div class="grid grid-cols-2 gap-2">
          <label
            class="group flex cursor-pointer items-center gap-3 rounded-xl border border-border bg-card px-3.5 py-3 transition-colors hover:border-success/60 has-[:checked]:border-success has-[:checked]:bg-success/10 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring"
          >
            <input type="radio" class="sr-only" name="type" formControlName="type" [value]="TYPE_GENERAL" />
            <span
              class="flex size-8 flex-none items-center justify-center rounded-full bg-success/15 text-success transition-colors group-has-[:checked]:bg-success group-has-[:checked]:text-primary-foreground"
              aria-hidden="true"
            >
              <hlm-icon name="lucidePlus" size="1rem" />
            </span>
            <span class="flex min-w-0 flex-col">
              <span class="text-base font-semibold text-foreground">General (Allgemein)</span>
              <span class="text-sm text-muted-foreground">Adds credit</span>
            </span>
          </label>
          <label
            class="group flex cursor-pointer items-center gap-3 rounded-xl border border-border bg-card px-3.5 py-3 transition-colors hover:border-destructive/60 has-[:checked]:border-destructive has-[:checked]:bg-destructive/10 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring"
          >
            <input type="radio" class="sr-only" name="type" formControlName="type" [value]="TYPE_EXPIRED" />
            <span
              class="flex size-8 flex-none items-center justify-center rounded-full bg-destructive/15 text-destructive transition-colors group-has-[:checked]:bg-destructive group-has-[:checked]:text-primary-foreground"
              aria-hidden="true"
            >
              <hlm-icon name="lucideMinus" size="1rem" />
            </span>
            <span class="flex min-w-0 flex-col">
              <span class="text-base font-semibold text-foreground">Expired (Verfallen)</span>
              <span class="text-sm text-muted-foreground">Writes credit off</span>
            </span>
          </label>
        </div>
      </fieldset>

      <!-- VALID UNTIL -->
      <div class="flex flex-col gap-2">
        <hlm-form-field>
          <label hlmLabel for="addCreditValidUntil">Valid until</label>
          <div class="flex flex-wrap items-center gap-2">
            <!-- w-auto: compact field sized to the date + calendar icon, next to the button. -->
            <input
              id="addCreditValidUntil"
              hlmInput
              type="date"
              class="w-auto"
              formControlName="validUntil"
              [class.opacity-50]="noDateSig()"
            />
            @if (invoicingDayIso(); as invoicingDay) {
              <button
                type="button"
                hlmBtn
                variant="accent"
                data-testid="set-invoicing-day"
                [disabled]="noDateSig()"
                (click)="setToInvoicingDay()"
              >
                <hlm-icon name="lucideCalendar" size="0.9rem" />
                Set to invoicing day
              </button>
            }
          </div>
          @if (invoicingDayIso(); as invoicingDay) {
            <!-- Accent-tinted info box as elsewhere (hlm-alert warn: amber in Editorial, blue in
                 Clean). role="note": static information, not an assertive alert. -->
            <hlm-alert tone="warn" role="note" class="mt-2" data-testid="invoicing-day-info">
              <hlm-icon name="lucideInfo" size="1rem" class="mt-0.5 flex-none" />
              <span>Next invoicing day: <strong class="font-mono-feat">{{ invoicingDay | date: 'dd.MM.yyyy' }}</strong></span>
            </hlm-alert>
          }
        </hlm-form-field>
        <hlm-checkbox formControlName="noDate" label="No date" />
      </div>

      <!-- AMOUNT -->
      <hlm-form-field>
        <label hlmLabel for="addCreditAmount">
          Amount (€)
          <span class="text-destructive">*</span>
        </label>
        <input
          id="addCreditAmount"
          hlmInput
          formControlName="amountInput"
          inputmode="decimal"
          autocomplete="off"
          placeholder="0,00"
          (blur)="onAmountBlur()"
          [attr.aria-invalid]="form.controls.amountInput.touched && form.controls.amountInput.invalid"
        />
        @if (form.controls.amountInput.touched && form.controls.amountInput.errors?.['required']) {
          <p hlmFieldError>Please enter an amount.</p>
        } @else if (form.controls.amountInput.touched && form.controls.amountInput.errors?.['localeDecimal']) {
          <p hlmFieldError>Enter a valid number.</p>
        } @else if (form.controls.amountInput.touched && form.controls.amountInput.errors?.['zeroAmount']) {
          <p hlmFieldError>Amount must not be zero.</p>
        } @else {
          <p hlmFieldHint>Sign is set from the type (General +, Expired −). You can override it.</p>
        }
      </hlm-form-field>

      <!-- DESCRIPTION -->
      <hlm-form-field>
        <label hlmLabel for="addCreditReason">Description</label>
        <input
          id="addCreditReason"
          hlmInput
          formControlName="reason"
          [attr.maxlength]="reasonMaxLength"
          autocomplete="off"
          placeholder="Optional note for this credit"
        />
        <p hlmFieldHint class="text-right tabular-nums">{{ reasonLength() }}/{{ reasonMaxLength }}</p>
      </hlm-form-field>
    </form>
  </section>

  <footer hlmDialogFooter>
    <button type="button" hlmBtn variant="outline" (click)="cancel()">Cancel</button>
    <button type="button" hlmBtn variant="default" (click)="submit()" [disabled]="submitting()">
      @if (submitting()) {
        <hlm-spinner size="16" />
      } @else {
        <hlm-icon name="lucideCheck" size="0.9rem" />
      }
      Save Entry
    </button>
  </footer>
</hlm-dialog-surface>
